import { useCallback, useState } from "react";
import useApi from "./useApi";
import { API } from "../utils/constants";

/**
 * Result classifications for a booking submission (CON-001 §3, CON-002 §5A).
 * @readonly
 */
export const BOOKING_RESULT = {
  SUCCESS: "success", // 202 — request accepted and emailed (no body)
  VALIDATION: "validation", // 422 — ProblemDetails.errors field map
  EMAIL: "email", // 502 — email delivery failed (SMTP unavailable)
  NETWORK: "network", // no response (offline / timeout)
  ERROR: "error", // any other status
};

/**
 * useCreateBooking — submits a booking request to `POST /api/bookings` (email-only,
 * CON-002 §5A) and classifies the outcome per the CON-001 §3 status table.
 *
 * The returned object is a tagged union on `result`:
 * - SUCCESS:    { result }
 * - VALIDATION: { result, fieldErrors: Record<string,string>, detail }
 * - EMAIL:      { result, detail }   (retryable — SMTP unavailable)
 * - NETWORK:    { result, detail }   (retryable)
 * - ERROR:      { result, detail }
 *
 * Never throws — always resolves to a classified result. `submitting` guards double-submit.
 *
 * @returns {{
 *   submitBooking: (payload: object) => Promise<object>,
 *   submitting: boolean,
 * }}
 */
export default function useCreateBooking() {
  const api = useApi();
  const [submitting, setSubmitting] = useState(false);

  const submitBooking = useCallback(
    async (payload) => {
      setSubmitting(true);
      try {
        await api.post(API.BOOKINGS.CREATE, payload);
        return { result: BOOKING_RESULT.SUCCESS };
      } catch (err) {
        // useApi's interceptor rejects with `error.response` (may be undefined
        // on network failure). Be defensive about either shape.
        const response = err?.response ?? err;
        const status = response?.status;
        const data = response?.data;

        if (!status) {
          return {
            result: BOOKING_RESULT.NETWORK,
            detail:
              "We couldn't reach the server. Check your connection and try again.",
          };
        }

        if (status === 422) {
          // Flatten ProblemDetails.errors (string[] per field) to first message.
          const errors = data?.errors ?? {};
          const fieldErrors = {};
          Object.keys(errors).forEach((k) => {
            const v = errors[k];
            fieldErrors[k] = Array.isArray(v) ? v[0] : String(v);
          });
          return {
            result: BOOKING_RESULT.VALIDATION,
            fieldErrors,
            detail: data?.detail || "Please correct the highlighted fields.",
          };
        }

        if (status === 502) {
          return {
            result: BOOKING_RESULT.EMAIL,
            detail:
              "We couldn't send your request right now. Please try again in a few minutes.",
          };
        }

        return {
          result: BOOKING_RESULT.ERROR,
          detail:
            data?.detail || "Something went wrong sending your request. Please try again.",
        };
      } finally {
        setSubmitting(false);
      }
    },
    [api]
  );

  return { submitBooking, submitting };
}
